// ============================================================================
// SPECIALTY CATALOG V2 (display-layer routing catalog)
//
// Single source of truth for the Pack Manager specialty list. Moved out of
// PackManagerView.jsx (was the inline SPECIALTY_CATEGORIES, 24 cosmetic
// categories) into this shared module, reorganized around ReferralGate's
// ROUTING reality (6 V2 groups) instead of a flat real-world taxonomy.
//
// DISPLAY-LAYER ONLY. The durable key is `id` (=== Firestore specialtyId at
// tenants/{tenantId}/specialties/{specialtyId} and the parent of nested packs).
// This module NEVER renames or removes an id. `category` was never persisted
// (only `icon` is snapshotted at enable time), so regrouping is migration-safe.
//
// Each entry preserves its legacy category (legacyCategoryId/legacyCategoryName)
// and icon so the enable flow keeps writing the same Firestore shape
// (specialtyId, name, description, icon, status, sortOrder, activePack).
//
// Catalog visibility and evidence-concept-module existence are DECOUPLED: a
// visible destination may use its OWN module, a PARENT module, shared-only
// concepts, or no module (source layer). See evidenceMode.
//
// Pure / Never-PHI. No Firebase, no Firestore, no side effects.
// ============================================================================

// ── Allowed enums (validated by specialtyCatalog.verify.js) ──────────────────
export const CATALOG_GROUPS = Object.freeze([
  'clinical_referral', 'surgical_procedural', 'diagnostic_service',
  'high_acuity_pathway', 'hidden_source', 'optional_track',
]);
export const CATALOG_STATUSES = Object.freeze([
  'default_visible', 'optional_track', 'service_pack',
  'high_acuity_pathway', 'hidden_source', 'deferred',
]);
export const CATALOG_ROLES = Object.freeze([
  'receiving_specialty', 'surgical_specialty', 'diagnostic_service',
  'acuity_pathway', 'source_layer',
]);
export const CATALOG_ROUTING_TYPES = Object.freeze([
  'clinical_consult', 'surgical_consult', 'service_request',
  'escalation_pathway', 'source_origin',
]);
export const CATALOG_EVIDENCE_MODES = Object.freeze([
  'own_module', 'parent_module', 'shared_only', 'no_module', 'future_candidate',
]);

// ── V2 display groups (ordered; rendered as accordions in Pack Manager) ──────
export const SPECIALTY_GROUPS = Object.freeze([
  { id: 'clinical_referral',   label: 'Clinical Referral Specialties',        icon: 'Stethoscope',    defaultExpanded: true },
  { id: 'surgical_procedural', label: 'Surgical & Procedural Specialties',    icon: 'Scissors',       defaultExpanded: false },
  { id: 'diagnostic_service',  label: 'Diagnostic / Service Packs',           icon: 'Microscope',     defaultExpanded: false },
  { id: 'high_acuity_pathway', label: 'High-Acuity Pathways',                 icon: 'AlertTriangle',  defaultExpanded: false },
  { id: 'optional_track',      label: 'Optional Tenant-Specific Tracks',      icon: 'Sparkles',       defaultExpanded: false },
  { id: 'hidden_source',       label: 'Advanced / Hidden Source Layers',      icon: 'Shield',         defaultExpanded: false },
]);

// ── entry factory (fills boilerplate; resolves conceptModuleId) ──────────────
const PRESETS = {
  clinical:    { group: 'clinical_referral',   status: 'default_visible',     role: 'receiving_specialty', routingType: 'clinical_consult',   evidenceMode: 'own_module',       parentSpecialtyId: null, defaultVisible: true,  tenantConfigurable: true },
  surgical:    { group: 'surgical_procedural', status: 'default_visible',     role: 'surgical_specialty',  routingType: 'surgical_consult',   evidenceMode: 'own_module',       parentSpecialtyId: null, defaultVisible: true,  tenantConfigurable: true },
  service:     { group: 'diagnostic_service',  status: 'service_pack',        role: 'diagnostic_service',  routingType: 'service_request',    evidenceMode: 'own_module',       parentSpecialtyId: null, defaultVisible: true,  tenantConfigurable: true },
  serviceOpt:  { group: 'diagnostic_service',  status: 'service_pack',        role: 'diagnostic_service',  routingType: 'service_request',    evidenceMode: 'parent_module',                             defaultVisible: false, tenantConfigurable: true },
  acuity:      { group: 'high_acuity_pathway', status: 'high_acuity_pathway', role: 'acuity_pathway',      routingType: 'escalation_pathway', evidenceMode: 'own_module',       parentSpecialtyId: null, defaultVisible: true,  tenantConfigurable: true },
  trackMed:    { group: 'optional_track',      status: 'optional_track',      role: 'receiving_specialty', routingType: 'clinical_consult',   evidenceMode: 'parent_module',                             defaultVisible: false, tenantConfigurable: true },
  trackSurg:   { group: 'optional_track',      status: 'optional_track',      role: 'surgical_specialty',  routingType: 'surgical_consult',   evidenceMode: 'parent_module',                             defaultVisible: false, tenantConfigurable: true },
  source:      { group: 'hidden_source',       status: 'hidden_source',       role: 'source_layer',        routingType: 'source_origin',      evidenceMode: 'no_module',        parentSpecialtyId: null, conceptModuleId: null, defaultVisible: false, tenantConfigurable: true },
  deferred:    { group: 'optional_track',      status: 'deferred',            role: 'receiving_specialty', routingType: 'clinical_consult',   evidenceMode: 'future_candidate', parentSpecialtyId: null, conceptModuleId: null, defaultVisible: false, tenantConfigurable: false },
};

const mk = (preset, cfg) => {
  const evidenceMode = cfg.evidenceMode || preset.evidenceMode;
  let conceptModuleId;
  if (Object.prototype.hasOwnProperty.call(cfg, 'conceptModuleId')) conceptModuleId = cfg.conceptModuleId;
  else if (evidenceMode === 'own_module') conceptModuleId = cfg.id;
  else if (Object.prototype.hasOwnProperty.call(preset, 'conceptModuleId')) conceptModuleId = preset.conceptModuleId;
  else conceptModuleId = null;
  return Object.freeze({
    id: cfg.id,
    label: cfg.label,
    description: cfg.description,
    group: cfg.group || preset.group,
    subgroup: cfg.legacyCategoryId,
    status: cfg.status || preset.status,
    role: cfg.role || preset.role,
    routingType: cfg.routingType || preset.routingType,
    conceptModuleId,
    evidenceMode,
    parentSpecialtyId: cfg.parentSpecialtyId !== undefined ? cfg.parentSpecialtyId
      : (preset.parentSpecialtyId !== undefined ? preset.parentSpecialtyId : null),
    visibility: Object.freeze({
      defaultVisible: cfg.defaultVisible !== undefined ? cfg.defaultVisible : preset.defaultVisible,
      tenantConfigurable: cfg.tenantConfigurable !== undefined ? cfg.tenantConfigurable : preset.tenantConfigurable,
    }),
    deprecated: false,
    notes: cfg.notes || cfg.description,
    legacyCategoryId: cfg.legacyCategoryId,
    legacyCategoryName: cfg.legacyCategoryName,
    icon: cfg.icon,
    sortOrder: cfg.sortOrder,
  });
};

// legacy category metadata (id / name / icon) preserved from the V1 catalog
const PC = ['primary_care', 'Primary Care & Internal Medicine', 'Stethoscope'];
const CV = ['cardiovascular', 'Cardiovascular', 'Heart'];
const NE = ['neurological', 'Neurological', 'Brain'];
const MS = ['musculoskeletal', 'Musculoskeletal', 'Bone'];
const GI = ['digestive', 'Digestive & GI', 'Zap'];
const RE = ['respiratory', 'Respiratory', 'Lung'];
const EN = ['endocrine', 'Endocrine & Metabolic', 'FlaskConical'];
const RN = ['renal', 'Renal & Urological', 'Droplet'];
const ON = ['oncology', 'Oncology', 'Radiation'];
const MH = ['mental_health', 'Mental Health', 'BrainCircuit'];
const SK = ['skin', 'Dermatology & Plastic Surgery', 'SunMedium'];
const EY = ['eye', 'Ophthalmology', 'Eye'];
const ET = ['ent', 'Ear, Nose & Throat', 'Ear'];
const WH = ['womens_health', "Women's Health", 'Baby'];
const ID = ['infectious', 'Infectious Disease & Immunology', 'Shield'];
const SG = ['surgery', 'General & Specialized Surgery', 'Scissors'];
const EM = ['emergency', 'Emergency & Critical Care', 'AlertTriangle'];
const RD = ['radiology', 'Radiology & Imaging', 'Microscope'];
const PA = ['pathology', 'Pathology & Laboratory', 'FlaskConical'];
const AN = ['anesthesia', 'Anesthesiology', 'Syringe'];
const DE = ['dental', 'Dental & Oral Surgery', 'Hand'];
const GE = ['genetics', 'Medical Genetics', 'BrainCircuit'];
const PL = ['palliative', 'Palliative & Hospice Care', 'HeartPulse'];
const PV = ['preventive', 'Preventive & Occupational Medicine', 'Shield'];

const e = (presetKey, id, label, description, legacy, sortOrder, over = {}) =>
  mk(PRESETS[presetKey], { id, label, description, legacyCategoryId: legacy[0], legacyCategoryName: legacy[1], icon: legacy[2], sortOrder, ...over });

// ── SPECIALTY_CATALOG — all 61 specialties (no id renamed, none removed) ─────
export const SPECIALTY_CATALOG = Object.freeze([
  // ───── Clinical Referral Specialties (default visible; own module) ─────
  e('clinical', 'internal_medicine', 'Internal Medicine', 'General adult medicine, complex diagnoses', PC, 10),
  e('clinical', 'geriatrics', 'Geriatrics', 'Elderly care, aging-related conditions', PC, 20),
  e('clinical', 'pediatrics', 'Pediatrics', 'Infant, child, and adolescent care', PC, 30),
  e('clinical', 'cardiology', 'Cardiology', 'Heart disease, arrhythmias, heart failure', CV, 40),
  e('clinical', 'neurology', 'Neurology', 'Headaches, tremors, seizures, stroke, MS', NE, 50),
  e('clinical', 'pain_management', 'Pain Management', 'Chronic pain, interventional procedures', NE, 60),
  e('clinical', 'rheumatology', 'Rheumatology', 'Arthritis, lupus, autoimmune conditions', MS, 70),
  e('clinical', 'physical_medicine', 'Physical Medicine & Rehab', 'Rehabilitation, disability, function restoration', MS, 80),
  e('clinical', 'sports_medicine', 'Sports Medicine', 'Athletic injuries, performance optimization', MS, 90, { notes: 'Sport-specific assessment context, not every sports injury.' }),
  e('clinical', 'gastroenterology', 'Gastroenterology', 'GI disorders, liver disease, IBD, endoscopy', GI, 100),
  e('clinical', 'hepatology', 'Hepatology', 'Liver disease, hepatitis, cirrhosis', GI, 110),
  e('clinical', 'pulmonology', 'Pulmonology', 'Lung disease, COPD, asthma, sleep apnea', RE, 120),
  e('clinical', 'sleep_medicine', 'Sleep Medicine', 'Sleep disorders, insomnia, sleep apnea', RE, 130),
  e('clinical', 'endocrinology', 'Endocrinology', 'Diabetes, thyroid, hormonal disorders', EN, 140),
  e('clinical', 'nephrology', 'Nephrology', 'Kidney disease, dialysis, hypertension', RN, 150),
  e('clinical', 'urology', 'Urology', 'Urinary tract, prostate, kidney stones', RN, 160),
  e('clinical', 'oncology', 'Medical Oncology', 'Cancer diagnosis and chemotherapy', ON, 170),
  e('clinical', 'hematology', 'Hematology', 'Blood disorders, leukemia, lymphoma', ON, 180),
  e('clinical', 'psychiatry', 'Psychiatry', 'Mental health, depression, anxiety, psychosis', MH, 190),
  e('clinical', 'addiction_medicine', 'Addiction Medicine', 'Substance abuse, addiction treatment', MH, 200),
  e('clinical', 'dermatology', 'Dermatology', 'Skin conditions, lesions, rashes, melanoma', SK, 210),
  e('clinical', 'ophthalmology', 'Ophthalmology', 'Eye diseases, cataracts, glaucoma, retina', EY, 220),
  e('clinical', 'ent', 'ENT / Otolaryngology', 'Ear, nose, throat, sinus, hearing', ET, 230),
  e('clinical', 'obstetrics_gynecology', 'Obstetrics & Gynecology', 'Pregnancy, childbirth, reproductive health', WH, 240),
  e('clinical', 'maternal_fetal', 'Maternal-Fetal Medicine', 'High-risk pregnancy, fetal diagnosis', WH, 250),
  e('clinical', 'reproductive_endocrinology', 'Reproductive Endocrinology', 'Infertility, IVF, hormonal issues', WH, 260),
  e('clinical', 'infectious_disease', 'Infectious Disease', 'Infections, HIV, tropical diseases', ID, 270),
  e('clinical', 'allergy_immunology', 'Allergy & Immunology', 'Allergies, asthma, immune disorders', ID, 280),
  e('clinical', 'genetics', 'Medical Genetics', 'Genetic disorders, hereditary conditions', GE, 290),
  e('clinical', 'palliative_care', 'Palliative Care', 'Comfort care, symptom management', PL, 300),
  e('clinical', 'occupational_medicine', 'Occupational Medicine', 'Workplace health, injury prevention', PV, 310),

  // ───── Surgical & Procedural Specialties (default visible; own module) ─────
  e('surgical', 'cardiothoracic_surgery', 'Cardiothoracic Surgery', 'Heart and chest surgery, bypass, valve repair', CV, 10),
  e('surgical', 'vascular_surgery', 'Vascular Surgery', 'Blood vessel disorders, aneurysms, PAD', CV, 20),
  e('surgical', 'neurosurgery', 'Neurosurgery', 'Brain and spine surgery, tumors', NE, 30),
  e('surgical', 'orthopedics', 'Orthopedics', 'Bones, joints, fractures, sports injuries', MS, 40),
  e('surgical', 'colorectal_surgery', 'Colorectal Surgery', 'Colon, rectal, and anal disorders', GI, 50),
  e('surgical', 'thoracic_surgery', 'Thoracic Surgery', 'Lung surgery, esophageal surgery', RE, 60),
  e('surgical', 'general_surgery', 'General Surgery', 'Abdominal surgery, hernias, appendectomy', SG, 70),
  e('surgical', 'anesthesiology', 'Anesthesiology', 'Surgical anesthesia, perioperative care', AN, 80, { notes: 'Perioperative referral; default-visible but tenant-configurable.' }),

  // ───── Diagnostic / Service Packs ─────
  e('service', 'radiology', 'Radiology', 'Medical imaging, X-ray, CT, MRI', RD, 10, { notes: 'Service-pack (order/report), not a consult.' }),
  e('service', 'pathology', 'Pathology', 'Disease diagnosis, biopsy analysis', PA, 20, { notes: 'Service-pack (specimen report), not a consult.' }),
  e('service', 'clinical_lab', 'Clinical Laboratory', 'Lab tests, blood work interpretation', PA, 30, { notes: 'Service-pack (lab result), not a consult.' }),
  e('serviceOpt', 'interventional_radiology', 'Interventional Radiology', 'Image-guided procedures, embolization', RD, 40, { parentSpecialtyId: 'radiology', conceptModuleId: 'radiology' }),
  e('serviceOpt', 'nuclear_medicine', 'Nuclear Medicine', 'PET scans, radioactive treatments', RD, 50, { parentSpecialtyId: 'radiology', conceptModuleId: 'radiology' }),
  e('serviceOpt', 'audiology', 'Audiology', 'Hearing disorders, hearing aids', ET, 60, { parentSpecialtyId: 'ent', conceptModuleId: 'ent' }),
  e('serviceOpt', 'optometry', 'Optometry', 'Vision care, glasses, contact lenses', EY, 70, { parentSpecialtyId: 'ophthalmology', conceptModuleId: 'ophthalmology' }),

  // ───── High-Acuity Pathways ─────
  e('acuity', 'emergency_medicine', 'Emergency Medicine', 'Acute emergencies, trauma, urgent care', EM, 10, { notes: 'Escalation/routing surface; may map to Gate URGENT/REDIRECT.' }),
  e('acuity', 'critical_care', 'Critical Care / ICU', 'Intensive care, life support, sepsis', EM, 20, { notes: 'Escalation/routing surface, not ordinary outpatient referral.' }),

  // ───── Hidden Source Layers ─────
  e('source', 'family_medicine', 'Family Medicine', 'Comprehensive care for all ages', PC, 10, { notes: 'GP/source layer; opt-in only if tenant receives Family Medicine referrals.' }),

  // ───── Optional Tenant-Specific Tracks (hidden by default; parent module) ─────
  e('trackMed', 'radiation_oncology', 'Radiation Oncology', 'Radiation therapy for cancer', ON, 10, { parentSpecialtyId: 'oncology', conceptModuleId: 'oncology' }),
  e('trackSurg', 'surgical_oncology', 'Surgical Oncology', 'Cancer surgery, tumor removal', ON, 20, { parentSpecialtyId: 'oncology', conceptModuleId: 'oncology' }),
  e('trackMed', 'gynecologic_oncology', 'Gynecologic Oncology', 'Gynecologic cancers', WH, 30, { parentSpecialtyId: 'obstetrics_gynecology', conceptModuleId: 'obstetrics_gynecology' }),
  e('trackMed', 'child_psychiatry', 'Child & Adolescent Psychiatry', 'Pediatric mental health, ADHD, autism', MH, 40, { parentSpecialtyId: 'psychiatry', conceptModuleId: 'psychiatry' }),
  e('trackSurg', 'plastic_surgery', 'Plastic Surgery', 'Reconstructive and cosmetic surgery', SK, 50, { parentSpecialtyId: 'general_surgery', conceptModuleId: 'general_surgery' }),
  e('trackSurg', 'oral_surgery', 'Oral & Maxillofacial Surgery', 'Jaw surgery, wisdom teeth, facial trauma', DE, 60, { parentSpecialtyId: 'ent', conceptModuleId: 'ent' }),
  e('trackSurg', 'trauma_surgery', 'Trauma Surgery', 'Emergency surgery, critical injuries', SG, 70, { parentSpecialtyId: 'general_surgery', conceptModuleId: 'general_surgery' }),
  e('trackSurg', 'bariatric_surgery', 'Bariatric Surgery', 'Weight loss surgery, gastric bypass', SG, 80, { parentSpecialtyId: 'general_surgery', conceptModuleId: 'general_surgery' }),
  e('trackSurg', 'transplant_surgery', 'Transplant Surgery', 'Organ transplantation', SG, 90, { parentSpecialtyId: 'general_surgery', conceptModuleId: 'general_surgery' }),
  e('trackMed', 'metabolism', 'Metabolism & Nutrition', 'Obesity, metabolic syndrome, nutrition', EN, 100, { parentSpecialtyId: 'endocrinology', conceptModuleId: 'endocrinology' }),
  e('trackMed', 'hospice', 'Hospice Medicine', 'End-of-life care', PL, 110, { parentSpecialtyId: 'palliative_care', conceptModuleId: 'palliative_care' }),

  // ───── Deferred (no distinct receiving surface yet; not tenant-configurable) ─────
  e('deferred', 'preventive_medicine', 'Preventive Medicine', 'Disease prevention, public health', PV, 120, { notes: 'Deferred; no distinct receiving surface yet.' }),
]);

// ── fast index ───────────────────────────────────────────────────────────────
const CATALOG_INDEX = new Map(SPECIALTY_CATALOG.map((s) => [s.id, s]));

// ============================================================================
// PUBLIC HELPERS (pure)
// ============================================================================

/** @returns {Object|null} the catalog entry for a specialtyId, or null. */
export const getCatalogEntry = (id) =>
  (typeof id === 'string' && CATALOG_INDEX.get(id)) || null;

/** @returns {Array} entries flagged defaultVisible (clinical/surgical/default service packs/high-acuity). */
export const getDefaultVisibleSpecialties = () =>
  SPECIALTY_CATALOG.filter((s) => s.visibility.defaultVisible === true);

/**
 * @returns {Array<{id,label,icon,specialties}>} the V2 groups, in display order,
 * each with its catalog entries sorted by sortOrder. Empty groups are kept
 * (stable rendering).
 */
export const getSpecialtiesByGroup = () =>
  SPECIALTY_GROUPS.map((g) => ({
    id: g.id,
    label: g.label,
    icon: g.icon,
    defaultExpanded: g.defaultExpanded,
    specialties: SPECIALTY_CATALOG
      .filter((s) => s.group === g.id)
      .sort((a, b) => a.sortOrder - b.sortOrder),
  }));

/** @returns {Array} entries with the given status. */
export const getSpecialtiesByStatus = (status) =>
  SPECIALTY_CATALOG.filter((s) => s.status === status);

/**
 * Catalog entries that should be surfaced to a tenant: default-visible OR
 * already enabled for that tenant (so enabled-but-hidden/optional specialties
 * never disappear). `enabledSpecialties` is the map from getSpecialties(tenantId).
 * @param {Object} enabledSpecialties map keyed by specialtyId
 * @returns {Array}
 */
export const getEnabledVisibleCatalogEntries = (enabledSpecialties = {}) =>
  SPECIALTY_CATALOG.filter(
    (s) => s.visibility.defaultVisible === true || !!enabledSpecialties[s.id],
  );

/**
 * Resolve which concept module powers a specialty's recognition (own id for
 * own_module, the parent's module for parent_module, null otherwise).
 * @returns {string|null}
 */
export const resolveConceptModule = (id) => getCatalogEntry(id)?.conceptModuleId ?? null;
